import { XYCoord } from 'react-dnd'
import findKey from 'lodash/findKey'

import { ItemRefType } from '../types'

const itemMargin = 3

interface ItemCoords {
	name: string
	x1: number
	x2: number
	xMiddle: number
	y1: number
	y2: number
	width: number
	height: number
	overItemVertically: (xy: XYCoord, height: number) => boolean
	overItemOnLeft: (xy: XYCoord, width: number) => boolean
	overItemOnRight: (xy: XYCoord, width: number) => boolean
}

class DragDropService {
	itemCoords: Record<Key, ItemCoords> = {}

	updateItemCoords(refs: ItemRefType) {
		if (!Object.keys(refs).length || !Object.values(refs).every(({ current }) => current)) {
			return
		}

		this.resetItemCoords()
		this.setUpItemCoords(refs)
	}

	setUpItemCoords(refs: ItemRefType) {
		for (const key in refs) {
			const rect = refs[key]!.current!.getBoundingClientRect()

			this.itemCoords[key] = {
				name: key,
				x1: Math.floor(rect.left),
				x2: Math.floor(rect.right),
				xMiddle: Math.floor(rect.left + rect.width / 2),
				y1: Math.floor(rect.top),
				y2: Math.floor(rect.bottom),
				width: Math.floor(rect.width),
				height: Math.floor(rect.height),
				overItemVertically({ y }: XYCoord, height: number) {
					const from = this.y1
					const to = this.y2
					return (y >= from && y <= to) || (y + height >= from && y + height <= to)
				},
				overItemOnRight({ x }: XYCoord, width: number) {
					const from = width < this.width ? this.xMiddle : this.x1
					const to = this.x2 + itemMargin

					return x >= from && x <= to
				},
				overItemOnLeft({ x }: XYCoord, width: number) {
					const from = this.x1 - itemMargin
					const to = width < this.width ? this.xMiddle : this.x2

					return x + width >= from && x + width <= to
				}
			}
		}
	}

	resetItemCoords() {
		this.itemCoords = {}
	}

	getPotentialDropTarget(draggable: { name: string; x: number; y: number }) {
		const { name, ...coords } = draggable

		const { width: w, height: h } = this.itemCoords[name]
		const width = Math.floor(w)
		const height = Math.floor(h)

		const dropTarget = findKey(this.itemCoords, (item) => {
			const isNotSameItem = name !== item.name
			const isOverItemVertically = item.overItemVertically(coords, height)
			const isOverItemLeft = item.overItemOnLeft(coords, width)
			const isOverItemRight = item.overItemOnRight(coords, width)

			return isNotSameItem && isOverItemVertically && (isOverItemLeft || isOverItemRight)
		})

		if (!dropTarget) {
			return null
		}

		return {
			dropTarget,
			isAfter: this.itemCoords[dropTarget].overItemOnRight(coords, width),
			isBefore: this.itemCoords[dropTarget].overItemOnLeft(coords, width)
		}
	}
}

const dragDrop = new DragDropService()

export default dragDrop
